// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect, useRef } from 'react';
import flags from 'react-phone-number-input/flags';
import { getCountries, getCountryCallingCode, type Country } from 'react-phone-number-input';
import { AppIcon } from './AppIcon';

// All countries from libphonenumber-js, sorted by calling code
const phoneCountries: Country[] = getCountries().sort((a, b) => {
  const codeA = getCountryCallingCode(a);
  const codeB = getCountryCallingCode(b);
  return Number(codeA) - Number(codeB);
});

export const DEFAULT_PHONE_COUNTRY = 'IE';
export { phoneCountries };

type PhoneFlagDropdownProps = Readonly<{
  value: string;
  onSelect: (code: string, dialCode: string) => void;
  disabled?: boolean;
}>;

export function PhoneFlagDropdown({ value, onSelect, disabled = false }: PhoneFlagDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SelectedFlag = flags[value as keyof typeof flags];

  const handleSelect = (countryCode: string) => {
    const dialCode = getCountryCallingCode(countryCode as Parameters<typeof getCountryCallingCode>[0]);
    onSelect(countryCode, String(dialCode));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        tabIndex={0}
        disabled={disabled}
        className={`h-[48px] px-4 border border-grey-04 bg-white flex items-center justify-between gap-4 outline-none focus:border-[var(--input-border-active)] focus-visible:shadow-[var(--input-focus-ring)] transition-[box-shadow,border-color] ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${open ? 'rounded-t-[8px] border-b-0' : 'rounded-lg'}`}
        onClick={() => { if (!disabled) setOpen(!open); }}
      >
        <span className="w-[28px] h-[20px]">
          {SelectedFlag && <SelectedFlag title={value} />}
        </span>
        <AppIcon
          name="chevron compact down"
          size={16}
          color="#141414"
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 z-50 w-full bg-white border border-grey-04 border-t-0 rounded-b-[8px] max-h-[240px] overflow-y-auto flex flex-col p-1">
          {phoneCountries.map(c => {
            const Flag = flags[c];
            if (!Flag) return null;
            const code = getCountryCallingCode(c);
            return (
              <button
                key={c}
                type="button"
                className={`w-full h-[28px] px-3 rounded hover:bg-gray-100 flex items-center gap-2 shrink-0 ${c === value ? 'bg-blue-50 ring-1 ring-blue-500' : ''}`}
                onClick={() => handleSelect(c)}
                title={c}
              >
                <span className="w-[28px] h-[20px] flex-shrink-0"><Flag title={c} /></span>
                <span className="text-xs text-grey-04 font-sans">+{code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Helper to get the default phone value with dial code */
export function getDefaultPhoneValue(countryCode: string = DEFAULT_PHONE_COUNTRY): string {
  return `+${getCountryCallingCode(countryCode as Parameters<typeof getCountryCallingCode>[0])}`;
}
